import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Zap, Plus, MapPin, Clock, MessageSquare, ChevronRight, X, Filter,
  DollarSign, Hammer, Home, FileCheck, TrendingUp, Eye, Send, Check,
  AlertCircle, RefreshCw, ArrowLeft, Lock, Users
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Type Config ───────────────────────────────────────────────────────────────
const TYPE_CONFIG = {
  lender:     { label: "Funding",    color: "text-blue-400",    border: "border-blue-500/30",    bg: "bg-blue-500/10",    badge: "bg-blue-500/15 text-blue-300 border-blue-500/30",    icon: DollarSign },
  contractor: { label: "Contractor", color: "text-orange-400",  border: "border-orange-500/30",  bg: "bg-orange-500/10",  badge: "bg-orange-500/15 text-orange-300 border-orange-500/30",  icon: Hammer },
  realtor:    { label: "Realtor",    color: "text-emerald-400", border: "border-emerald-500/30", bg: "bg-emerald-500/10", badge: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: Home },
  title:      { label: "Title",      color: "text-purple-400",  border: "border-purple-500/30",  bg: "bg-purple-500/10",  badge: "bg-purple-500/15 text-purple-300 border-purple-500/30",  icon: FileCheck },
};

const TABS = [
  { key: "all",        label: "Open Requests" },
  { key: "lender",     label: "Funding Requests" },
  { key: "contractor", label: "Contractor Bids" },
  { key: "realtor",    label: "Realtor Opportunities" },
  { key: "title",      label: "Title Requests" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Math.floor((Date.now() - new Date(isoStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function getKeyMetric(req) {
  const m = req.metadata || {};
  switch (req.request_type) {
    case "lender":
      return m.loan_amount ? `$${Number(m.loan_amount).toLocaleString()} loan` : null;
    case "contractor":
      return m.budget_max ? `Up to $${Number(m.budget_max).toLocaleString()}` : null;
    case "realtor":
      return m.expected_price ? `$${Number(m.expected_price).toLocaleString()} est.` : null;
    case "title":
      return m.transaction_type ? m.transaction_type.replace(/_/g, " ") : null;
    default: return null;
  }
}

// ── Request Card ─────────────────────────────────────────────────────────────
function RequestCard({ req, user, onViewOffers, onSubmitOffer }) {
  const cfg = TYPE_CONFIG[req.request_type] || TYPE_CONFIG.lender;
  const Icon = cfg.icon;
  const isOwn = req.user_id === user?.user_id;
  const hasRole = user?.extra_roles?.includes(req.request_type) || user?.is_admin;
  const keyMetric = getKeyMetric(req);

  const statusColors = {
    open:      "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    in_review: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    matched:   "bg-blue-500/10 text-blue-400 border-blue-500/30",
    closed:    "bg-white/5 text-white/30 border-white/10",
  };

  return (
    <div
      data-testid={`exchange-request-${req.request_id}`}
      className={`bg-[#0A0A0A] border ${cfg.border} hover:border-opacity-60 transition-all duration-200 hover:translate-y-[-1px] flex flex-col`}
    >
      {/* Header row */}
      <div className="p-4 pb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border ${cfg.badge} rounded-sm`}>
            <Icon className="w-3 h-3" />
            {cfg.label}
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] border rounded-sm ${statusColors[req.status] || statusColors.open}`}>
            {req.status.replace("_", " ")}
          </span>
          {isOwn && (
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] rounded-sm">
              Your Request
            </span>
          )}
        </div>
        {keyMetric && (
          <span className={`text-sm font-bold ${cfg.color} shrink-0`}>{keyMetric}</span>
        )}
      </div>

      {/* Body */}
      <div className="px-4 pb-3 flex-1">
        <h3 className="text-[15px] font-semibold text-white mb-1 leading-snug line-clamp-2">{req.title}</h3>
        {req.description && (
          <p className="text-xs text-white/50 line-clamp-2 mb-3">{req.description}</p>
        )}

        {/* Meta chips */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[11px] text-white/40">
            <MapPin className="w-3 h-3" />{req.location}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-white/30">
            <Clock className="w-3 h-3" />{timeAgo(req.created_at)}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-white/30">
            <Users className="w-3 h-3" />{req.user_name || "Investor"}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-white/[0.04] flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-[11px] text-white/30">
          <MessageSquare className="w-3 h-3" />
          {req.response_count || 0} {req.response_count === 1 ? "offer" : "offers"}
        </span>

        <div className="flex items-center gap-2">
          {isOwn ? (
            <Button
              size="sm"
              onClick={() => onViewOffers(req)}
              className="h-7 px-3 text-xs bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30"
              data-testid={`view-offers-${req.request_id}`}
            >
              <Eye className="w-3 h-3 mr-1" /> View Offers
            </Button>
          ) : hasRole ? (
            <Button
              size="sm"
              onClick={() => onSubmitOffer(req)}
              className={`h-7 px-3 text-xs border ${cfg.border} ${cfg.bg} ${cfg.color} hover:opacity-90`}
              data-testid={`submit-offer-${req.request_id}`}
            >
              <Send className="w-3 h-3 mr-1" /> Submit Offer
            </Button>
          ) : (
            <Link to="/upgrade">
              <Button
                size="sm"
                className="h-7 px-3 text-xs bg-white/5 border border-white/10 text-white/40 hover:bg-white/10"
                data-testid={`locked-offer-${req.request_id}`}
              >
                <Lock className="w-3 h-3 mr-1" /> Add {cfg.label} Role
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Create Request Modal ──────────────────────────────────────────────────────
function CreateRequestModal({ open, onClose, user, userDeals, onCreated }) {
  const [form, setForm] = useState({
    request_type: "lender", title: "", description: "", location: "",
    deal_id: "", metadata: {}
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setMeta = (k, v) => setForm(p => ({ ...p, metadata: { ...p.metadata, [k]: v } }));

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.location.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        request_type: form.request_type,
        title: form.title,
        description: form.description,
        location: form.location,
        deal_id: form.deal_id || null,
        metadata: form.metadata,
      };
      const res = await axios.post(`${BACKEND_URL}/api/exchange/requests`, payload, { withCredentials: true });
      toast.success("Request posted to Exchange!");
      onCreated(res.data.request);
      onClose();
      setForm({ request_type: "lender", title: "", description: "", location: "", deal_id: "", metadata: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#D4AF37]" /> Create Exchange Request
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Request Type */}
          <div>
            <Label className="text-xs text-white/60 uppercase tracking-wider mb-2 block">Request Type *</Label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button
                    key={key}
                    onClick={() => set("request_type", key)}
                    data-testid={`type-select-${key}`}
                    className={`p-3 border rounded text-center transition-all ${
                      form.request_type === key
                        ? `${cfg.border} ${cfg.bg} ${cfg.color}`
                        : "border-white/10 text-white/40 hover:border-white/20"
                    }`}
                  >
                    <Icon className="w-4 h-4 mx-auto mb-1" />
                    <span className="text-[11px] font-medium">{cfg.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Attach to Deal */}
          {userDeals?.length > 0 && (
            <div>
              <Label className="text-xs text-white/60 uppercase tracking-wider mb-2 block">Attach to Deal (optional)</Label>
              <Select value={form.deal_id} onValueChange={v => set("deal_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select a deal..." />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10">
                  <SelectItem value="_none">No deal — standalone request</SelectItem>
                  {userDeals.map(d => (
                    <SelectItem key={d.deal_id} value={d.deal_id}>
                      {d.property_address || d.deal_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Title */}
          <div>
            <Label className="text-xs text-white/60 uppercase tracking-wider mb-2 block">Title *</Label>
            <Input
              value={form.title}
              onChange={e => set("title", e.target.value)}
              placeholder="e.g. Need hard money lender for 3-unit flip in Atlanta"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              data-testid="exchange-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs text-white/60 uppercase tracking-wider mb-2 block">Description *</Label>
            <Textarea
              value={form.description}
              onChange={e => set("description", e.target.value)}
              placeholder="Describe what you need, timeline, deal details..."
              rows={3}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
              data-testid="exchange-description-input"
            />
          </div>

          {/* Type-specific fields */}
          {form.request_type === "lender" && (
            <div className="grid grid-cols-2 gap-4 p-4 border border-blue-500/20 bg-blue-500/5 rounded">
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Loan Amount ($)</Label>
                <Input type="number" value={form.metadata.loan_amount || ""} onChange={e => setMeta("loan_amount", e.target.value)}
                  placeholder="250000" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Deal Type</Label>
                <Select value={form.metadata.deal_type || ""} onValueChange={v => setMeta("deal_type", v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10">
                    {["Fix & Flip", "Buy & Hold", "BRRRR", "Land", "Commercial", "New Construction"].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Timeline (days)</Label>
                <Input type="number" value={form.metadata.timeline_days || ""} onChange={e => setMeta("timeline_days", e.target.value)}
                  placeholder="30" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Exit Strategy</Label>
                <Input value={form.metadata.exit_strategy || ""} onChange={e => setMeta("exit_strategy", e.target.value)}
                  placeholder="Refinance, sale, etc." className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
            </div>
          )}

          {form.request_type === "contractor" && (
            <div className="grid grid-cols-2 gap-4 p-4 border border-orange-500/20 bg-orange-500/5 rounded">
              <div className="col-span-2">
                <Label className="text-xs text-white/50 mb-1 block">Scope of Work</Label>
                <Textarea value={form.metadata.scope_of_work || ""} onChange={e => setMeta("scope_of_work", e.target.value)}
                  placeholder="Full rehab, kitchen + 2 baths, roof, HVAC..." rows={2}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Budget Min ($)</Label>
                <Input type="number" value={form.metadata.budget_min || ""} onChange={e => setMeta("budget_min", e.target.value)}
                  placeholder="25000" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Budget Max ($)</Label>
                <Input type="number" value={form.metadata.budget_max || ""} onChange={e => setMeta("budget_max", e.target.value)}
                  placeholder="45000" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Timeline (days)</Label>
                <Input type="number" value={form.metadata.timeline_days || ""} onChange={e => setMeta("timeline_days", e.target.value)}
                  placeholder="45" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
            </div>
          )}

          {form.request_type === "realtor" && (
            <div className="grid grid-cols-2 gap-4 p-4 border border-emerald-500/20 bg-emerald-500/5 rounded">
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Listing Type</Label>
                <Select value={form.metadata.listing_type || ""} onValueChange={v => setMeta("listing_type", v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10">
                    <SelectItem value="sell">Sell Property</SelectItem>
                    <SelectItem value="buy">Buy Property</SelectItem>
                    <SelectItem value="both">Buy & Sell</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Expected Price ($)</Label>
                <Input type="number" value={form.metadata.expected_price || ""} onChange={e => setMeta("expected_price", e.target.value)}
                  placeholder="350000" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Timeline (days)</Label>
                <Input type="number" value={form.metadata.timeline_days || ""} onChange={e => setMeta("timeline_days", e.target.value)}
                  placeholder="60" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
            </div>
          )}

          {form.request_type === "title" && (
            <div className="grid grid-cols-2 gap-4 p-4 border border-purple-500/20 bg-purple-500/5 rounded">
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Transaction Type</Label>
                <Select value={form.metadata.transaction_type || ""} onValueChange={v => setMeta("transaction_type", v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10">
                    <SelectItem value="purchase">Purchase</SelectItem>
                    <SelectItem value="refinance">Refinance</SelectItem>
                    <SelectItem value="payoff">Payoff</SelectItem>
                    <SelectItem value="1031_exchange">1031 Exchange</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">State</Label>
                <Input value={form.metadata.state || ""} onChange={e => setMeta("state", e.target.value)}
                  placeholder="GA, TX, FL..." className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-white/50 mb-1 block">Notes</Label>
                <Textarea value={form.metadata.notes || ""} onChange={e => setMeta("notes", e.target.value)}
                  rows={2} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none" />
              </div>
            </div>
          )}

          {/* Location */}
          <div>
            <Label className="text-xs text-white/60 uppercase tracking-wider mb-2 block">Location (City, State) *</Label>
            <Input
              value={form.location}
              onChange={e => set("location", e.target.value)}
              placeholder="Atlanta, GA"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              data-testid="exchange-location-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="ghost" onClick={onClose} className="flex-1 border border-white/10 text-white/60 hover:text-white">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold"
              data-testid="create-request-submit-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Post Request
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Submit Offer Modal ────────────────────────────────────────────────────────
function SubmitOfferModal({ open, onClose, request, user, onSubmitted }) {
  const [form, setForm] = useState({ message: "", terms: {} });
  const [saving, setSaving] = useState(false);

  const setT = (k, v) => setForm(p => ({ ...p, terms: { ...p.terms, [k]: v } }));

  if (!request) return null;
  const cfg = TYPE_CONFIG[request.request_type] || TYPE_CONFIG.lender;

  const handleSubmit = async () => {
    if (!form.message.trim() || form.message.length < 10) {
      toast.error("Please write a message (min 10 characters)");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/exchange/requests/${request.request_id}/respond`,
        { message: form.message, terms: form.terms },
        { withCredentials: true }
      );
      toast.success("Offer submitted successfully!");
      onSubmitted();
      onClose();
      setForm({ message: "", terms: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit offer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-bold flex items-center gap-2">
            <Send className={`w-4 h-4 ${cfg.color}`} /> Submit Offer
          </DialogTitle>
          <p className="text-xs text-white/40 mt-1 line-clamp-2">{request.title}</p>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Type-specific offer fields */}
          {request.request_type === "lender" && (
            <div className="grid grid-cols-2 gap-4 p-4 border border-blue-500/20 bg-blue-500/5 rounded">
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Loan Amount ($)</Label>
                <Input type="number" value={form.terms.loan_amount || ""} onChange={e => setT("loan_amount", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Interest Rate (%)</Label>
                <Input type="number" step="0.1" value={form.terms.interest_rate || ""} onChange={e => setT("interest_rate", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Term (months)</Label>
                <Input type="number" value={form.terms.term_months || ""} onChange={e => setT("term_months", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">LTV (%)</Label>
                <Input type="number" value={form.terms.ltv || ""} onChange={e => setT("ltv", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
          )}

          {request.request_type === "contractor" && (
            <div className="grid grid-cols-2 gap-4 p-4 border border-orange-500/20 bg-orange-500/5 rounded">
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Bid Amount ($)</Label>
                <Input type="number" value={form.terms.bid_amount || ""} onChange={e => setT("bid_amount", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Timeline (days)</Label>
                <Input type="number" value={form.terms.timeline_days || ""} onChange={e => setT("timeline_days", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-white/50 mb-1 block">Notes</Label>
                <Input value={form.terms.notes || ""} onChange={e => setT("notes", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
          )}

          {request.request_type === "realtor" && (
            <div className="grid grid-cols-2 gap-4 p-4 border border-emerald-500/20 bg-emerald-500/5 rounded">
              <div className="col-span-2">
                <Label className="text-xs text-white/50 mb-1 block">Strategy</Label>
                <Input value={form.terms.strategy || ""} onChange={e => setT("strategy", e.target.value)}
                  placeholder="e.g. Off-market listing + investor network" className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Commission (%)</Label>
                <Input type="number" step="0.1" value={form.terms.commission_pct || ""} onChange={e => setT("commission_pct", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Timeline (days)</Label>
                <Input type="number" value={form.terms.timeline_days || ""} onChange={e => setT("timeline_days", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
          )}

          {request.request_type === "title" && (
            <div className="grid grid-cols-2 gap-4 p-4 border border-purple-500/20 bg-purple-500/5 rounded">
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Estimated Fees ($, optional)</Label>
                <Input type="number" value={form.terms.fees || ""} onChange={e => setT("fees", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Notes</Label>
                <Input value={form.terms.notes || ""} onChange={e => setT("notes", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
          )}

          {/* Message */}
          <div>
            <Label className="text-xs text-white/60 uppercase tracking-wider mb-2 block">Your Message *</Label>
            <Textarea
              value={form.message}
              onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
              placeholder="Introduce yourself and explain why you're the right fit..."
              rows={4}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
              data-testid="offer-message-input"
            />
          </div>

          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} className="flex-1 border border-white/10 text-white/60">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className={`flex-1 font-bold text-white border ${cfg.border} ${cfg.bg} hover:opacity-90`}
              data-testid="submit-offer-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Offer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── View Offers Modal ─────────────────────────────────────────────────────────
function ViewOffersModal({ open, onClose, request, onAccept, onReject }) {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    if (open && request) fetchResponses();
  }, [open, request]);

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/exchange/requests/${request.request_id}/responses`,
        { withCredentials: true }
      );
      setResponses(res.data.responses || []);
    } catch {
      toast.error("Failed to load offers");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (responseId) => {
    setActing(responseId);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/exchange/requests/${request.request_id}/responses/${responseId}/accept`,
        {},
        { withCredentials: true }
      );
      toast.success(`Matched with ${res.data.matched_provider}!`);
      onAccept && onAccept();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept");
    } finally {
      setActing(null);
    }
  };

  const handleReject = async (responseId) => {
    setActing(responseId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/exchange/requests/${request.request_id}/responses/${responseId}/reject`,
        {},
        { withCredentials: true }
      );
      toast.success("Offer rejected");
      setResponses(prev => prev.map(r => r.response_id === responseId ? { ...r, status: "rejected" } : r));
    } catch {
      toast.error("Failed to reject");
    } finally {
      setActing(null);
    }
  };

  if (!request) return null;
  const cfg = TYPE_CONFIG[request.request_type] || TYPE_CONFIG.lender;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-bold flex items-center gap-2">
            <Eye className="w-4 h-4 text-[#D4AF37]" /> Offers — {request.title}
          </DialogTitle>
          <p className="text-xs text-white/40">{responses.length} offer{responses.length !== 1 ? "s" : ""} received</p>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-white/30" />
            </div>
          ) : responses.length === 0 ? (
            <div className="text-center py-12 text-white/30">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No offers yet</p>
              <p className="text-xs text-white/20 mt-1">Providers can submit offers from the Exchange</p>
            </div>
          ) : (
            responses.map(r => {
              const terms = r.terms || {};
              const isActing = acting === r.response_id;
              const isAccepted = r.status === "accepted";
              const isRejected = r.status === "rejected";

              return (
                <div
                  key={r.response_id}
                  data-testid={`offer-card-${r.response_id}`}
                  className={`border p-4 rounded ${
                    isAccepted ? "border-emerald-500/40 bg-emerald-500/5"
                    : isRejected ? "border-white/[0.04] opacity-50"
                    : `${cfg.border} bg-white/[0.02]`
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-sm font-semibold text-white">{r.provider_name}</p>
                      <p className="text-[11px] text-white/40">{timeAgo(r.created_at)}</p>
                    </div>
                    {isAccepted && (
                      <span className="px-2 py-1 text-[10px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-sm">
                        ACCEPTED
                      </span>
                    )}
                    {isRejected && (
                      <span className="px-2 py-1 text-[10px] bg-white/5 text-white/30 border border-white/10 rounded-sm">
                        REJECTED
                      </span>
                    )}
                  </div>

                  {/* Terms grid */}
                  {Object.keys(terms).length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {Object.entries(terms).map(([k, v]) => v && (
                        <div key={k} className="bg-white/[0.03] p-2 rounded">
                          <p className="text-[10px] text-white/30 capitalize">{k.replace(/_/g, " ")}</p>
                          <p className={`text-xs font-semibold ${cfg.color}`}>{v}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="text-xs text-white/60 mb-3">{r.message}</p>

                  {!isAccepted && !isRejected && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleAccept(r.response_id)}
                        disabled={isActing}
                        className="h-8 px-4 text-xs bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                        data-testid={`accept-offer-${r.response_id}`}
                      >
                        {isActing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                        Accept & Match
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleReject(r.response_id)}
                        disabled={isActing}
                        className="h-8 px-4 text-xs bg-white/5 hover:bg-red-500/10 text-white/50 hover:text-red-400 border border-white/10"
                        data-testid={`reject-offer-${r.response_id}`}
                      >
                        <X className="w-3 h-3 mr-1" /> Decline
                      </Button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main ExchangePage ─────────────────────────────────────────────────────────
export default function ExchangePage({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeType = searchParams.get("type") || "all";

  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [locationFilter, setLocationFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [userDeals, setUserDeals] = useState([]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeType !== "all") params.request_type = activeType;
      if (locationFilter) params.location = locationFilter;
      const res = await axios.get(`${BACKEND_URL}/api/exchange/requests`, { params, withCredentials: true });
      setRequests(res.data.requests || []);
      setCounts(res.data.counts || {});
    } catch {
      toast.error("Failed to load Exchange");
    } finally {
      setLoading(false);
    }
  }, [activeType, locationFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true })
      .then(r => setUserDeals(r.data?.deals || []))
      .catch(() => {});
  }, []);

  const setTab = (type) => setSearchParams({ type });

  const totalOpen = Object.values(counts).reduce((s, n) => s + (n || 0), 0);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-white/[0.06] bg-[#050505]/90 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <Link to="/network" className="text-white/40 hover:text-white flex items-center gap-1.5 text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Network</span>
            </Link>
            <div className="w-px h-5 bg-white/10 hidden sm:block" />
            <div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#D4AF37]" />
                <h1 className="text-base md:text-lg font-bold tracking-tight">Execution Exchange</h1>
              </div>
              <p className="text-[11px] text-white/30 mt-0.5 hidden md:block">Structured deal-driven service marketplace</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Stats strip */}
            <div className="hidden md:flex items-center gap-4 text-[11px] text-white/40 border border-white/[0.06] px-4 py-2 rounded">
              <span><span className="text-white font-bold">{totalOpen}</span> Open</span>
              <span className="text-blue-400"><span className="font-bold">{counts.lender || 0}</span> Funding</span>
              <span className="text-orange-400"><span className="font-bold">{counts.contractor || 0}</span> Contractor</span>
              <span className="text-emerald-400"><span className="font-bold">{counts.realtor || 0}</span> Realtor</span>
              <span className="text-purple-400"><span className="font-bold">{counts.title || 0}</span> Title</span>
            </div>

            <Button
              onClick={() => setShowCreate(true)}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold h-9 px-3 md:px-4"
              data-testid="create-request-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Create Request</span><span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>

        {/* Tabs - scrollable on mobile */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center gap-0 pb-0 overflow-x-auto scrollbar-none">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setTab(tab.key)}
              data-testid={`exchange-tab-${tab.key}`}
              className={`px-3 md:px-4 py-2.5 text-[12px] md:text-[13px] font-medium border-b-2 transition-all whitespace-nowrap flex-shrink-0 ${
                activeType === tab.key
                  ? "border-[#D4AF37] text-[#D4AF37]"
                  : "border-transparent text-white/40 hover:text-white/70"
              }`}
            >
              {tab.label}
              {tab.key !== "all" && counts[tab.key] > 0 && (
                <span className="ml-1.5 text-[10px] opacity-70">({counts[tab.key]})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Filter row */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-xs">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={locationFilter}
              onChange={e => setLocationFilter(e.target.value)}
              placeholder="Filter by location..."
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
              data-testid="location-filter"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchRequests}
            className="h-9 text-white/40 hover:text-white border border-white/[0.06]"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Request grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-52 bg-white/[0.02] border border-white/[0.04] animate-pulse rounded" />
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-24 border border-white/[0.04] bg-white/[0.01]">
            <Zap className="w-12 h-12 text-[#D4AF37]/30 mx-auto mb-4" />
            <p className="text-white/40 text-base mb-2">No requests yet</p>
            <p className="text-white/20 text-sm mb-6">Be the first to post a request to the Exchange</p>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold"
            >
              <Plus className="w-4 h-4 mr-2" /> Post First Request
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {requests.map(req => (
              <RequestCard
                key={req.request_id}
                req={req}
                user={user}
                onViewOffers={r => { setSelectedRequest(r); setShowOffersModal(true); }}
                onSubmitOffer={r => { setSelectedRequest(r); setShowOfferModal(true); }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateRequestModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        user={user}
        userDeals={userDeals}
        onCreated={r => setRequests(prev => [r, ...prev])}
      />
      <SubmitOfferModal
        open={showOfferModal}
        onClose={() => setShowOfferModal(false)}
        request={selectedRequest}
        user={user}
        onSubmitted={fetchRequests}
      />
      <ViewOffersModal
        open={showOffersModal}
        onClose={() => setShowOffersModal(false)}
        request={selectedRequest}
        onAccept={fetchRequests}
        onReject={() => {}}
      />
    </div>
  );
}
